'use client';
import { createContext, useContext, useEffect, useState } from 'react';
import { useToast } from '@/components/ui/ToastProvider';
import { toastSuccess } from '@/lib/toastHelpers.mjs';

const InquiryContext = createContext();

// Cookie utility functions
const COOKIE_NAME = 'trenchmate_inquiry_list';
const COOKIE_EXPIRY_DAYS = 7; // Cookie expires in 7 days

const saveToCookie = (inquiryItems) => {
    try {
        const cookieValue = JSON.stringify(inquiryItems);
        const expiryDate = new Date();
        expiryDate.setDate(expiryDate.getDate() + COOKIE_EXPIRY_DAYS);
        document.cookie = `${COOKIE_NAME}=${encodeURIComponent(
            cookieValue,
        )}; expires=${expiryDate.toUTCString()}; path=/; SameSite=Lax`;
    } catch (error) {
        try {
            console.warn(
                JSON.stringify({
                    ts: new Date().toISOString(),
                    level: 'warn',
                    service: 'inquiry_context',
                    event: 'save_cookie_failed',
                    message: String(error),
                }),
            );
        } catch (_e) {}
    }
};

const loadFromCookie = () => {
    try {
        if (typeof document === 'undefined') return []; // Server-side safety

        const cookies = document.cookie.split(';');
        const inquiryCookie = cookies.find((cookie) =>
            cookie.trim().startsWith(`${COOKIE_NAME}=`),
        );

        if (inquiryCookie) {
            const cookieValue = inquiryCookie.split('=')[1];
            const decodedValue = decodeURIComponent(cookieValue);
            const parsedItems = JSON.parse(decodedValue);

            // Validate the structure of loaded items
            if (Array.isArray(parsedItems)) {
                return parsedItems.filter(
                    (item) =>
                        item &&
                        typeof item === 'object' &&
                        item.name &&
                        typeof item.quantity === 'number' &&
                        item.quantity > 0,
                );
            }
        }
    } catch (error) {
        try {
            console.warn(
                JSON.stringify({
                    ts: new Date().toISOString(),
                    level: 'warn',
                    service: 'inquiry_context',
                    event: 'load_cookie_failed',
                    message: String(error),
                }),
            );
        } catch (_e) {}
    }
    return [];
};

const clearCookie = () => {
    try {
        document.cookie = `${COOKIE_NAME}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
    } catch (error) {
        try {
            console.warn(
                JSON.stringify({
                    ts: new Date().toISOString(),
                    level: 'warn',
                    service: 'inquiry_context',
                    event: 'clear_cookie_failed',
                    message: String(error),
                }),
            );
        } catch (_e) {}
    }
};

export const useInquiry = () => {
    const context = useContext(InquiryContext);
    if (!context) {
        throw new Error('useInquiry must be used within an InquiryProvider');
    }
    return context;
};

export const InquiryProvider = ({ children }) => {
    const [inquiryItems, setInquiryItems] = useState([]);
    // Overlay state moved to SidebarContext
    const [isLoaded, setIsLoaded] = useState(false);
    const toast = useToast();

    // Load inquiry items from cookie on mount
    useEffect(() => {
        const savedItems = loadFromCookie();
        if (savedItems.length > 0) {
            setInquiryItems(savedItems);
        }
        setIsLoaded(true);
    }, []);

    // Save to cookie whenever inquiry items change (but only after initial load)
    useEffect(() => {
        if (isLoaded) {
            saveToCookie(inquiryItems);
        }
    }, [inquiryItems, isLoaded]);

    const addToInquiry = (item) => {
        setInquiryItems((prev) => {
            // Generate a unique ID for the item
            let itemId;
            if (item.type === 'variant') {
                itemId = `variant-${item.productSlug}-${item.name}`;
            } else if (item.type === 'accessory') {
                itemId = `accessory-${item.name}`;
            } else {
                itemId = item.id || `item-${Date.now()}`;
            }

            // Check if item already exists
            const existingIndex = prev.findIndex((existing) => {
                if (existing.type !== item.type) return false;
                if (item.type === 'variant') {
                    return (
                        existing.name === item.name &&
                        existing.productName === item.productName &&
                        existing.productSlug === item.productSlug
                    );
                } else if (item.type === 'accessory') {
                    return existing.name === item.name;
                }
                return existing.id === itemId;
            });

            if (existingIndex >= 0) {
                // Update quantity if item exists
                const updated = [...prev];
                updated[existingIndex].quantity += 1;
                return updated;
            } else {
                // Add new item with generated ID
                const newItem = { ...item, id: itemId, quantity: 1 };

                // Show toast for new item
                setTimeout(() => {
                    toastSuccess(
                        toast?.addToast,
                        `${item.name || 'Item'} added to inquiry`,
                        'Added to Inquiry',
                    );
                }, 0);

                return [...prev, newItem];
            }
        });
    };

    const removeFromInquiry = (itemId) => {
        setInquiryItems((prev) => prev.filter((item) => item.id !== itemId));
    };

    const updateQuantity = (itemId, newQuantity) => {
        if (newQuantity <= 0) {
            removeFromInquiry(itemId);
            return;
        }

        setInquiryItems((prev) =>
            prev.map((item) => {
                if (item.id === itemId) {
                    return { ...item, quantity: newQuantity };
                }
                return item;
            }),
        );
    };

    const clearInquiry = () => {
        setInquiryItems([]);
        clearCookie();
    };

    const getTotalItems = () => {
        return inquiryItems.reduce((total, item) => total + item.quantity, 0);
    };

    const getInquiryText = () => {
        if (inquiryItems.length === 0) return '';

        let text = 'INQUIRY LIST:\n\n';

        inquiryItems.forEach((item, index) => {
            text += `${index + 1}. `;

            if (item.type === 'variant') {
                text += `${item.productName} - ${item.name}`;
            } else if (item.type === 'accessory') {
                text += `Accessory: ${item.name}`;
            } else {
                text += item.name;
            }

            text += ` (Quantity: ${item.quantity})\n`;

            if (item.description) {
                text += `   Description: ${item.description}\n`;
            }

            if (item.code) {
                text += `   Code: ${item.code}\n`;
            }

            if (
                item.specifications &&
                Object.keys(item.specifications).length > 0
            ) {
                text += `   Specifications: ${Object.entries(
                    item.specifications,
                )
                    .map(([key, value]) => {
                        // Capitalize the first letter of specification keys
                        const capitalizedKey =
                            key.charAt(0).toUpperCase() + key.slice(1);
                        return `${capitalizedKey}: ${value}`;
                    })
                    .join(', ')}\n`;
            }

            text += '\n';
        });

        text += `Total Items: ${getTotalItems()}\n\n`;

        return text;
    };

    // Legacy modal/submit/sidebar controls removed in favour of SidebarContext

    const isInInquiry = (item) => {
        return inquiryItems.some((existing) => {
            if (existing.type !== item.type) return false;
            if (item.type === 'variant') {
                return (
                    existing.name === item.name &&
                    existing.productName === item.productName &&
                    existing.productSlug === item.productSlug
                );
            } else if (item.type === 'accessory') {
                return existing.name === item.name;
            }
            return existing.id === item.id;
        });
    };

    const value = {
        inquiryItems,
        addToInquiry,
        removeFromInquiry,
        updateQuantity,
        clearInquiry,
        getTotalItems,
        getInquiryText,
        isInInquiry,
    };

    return (
        <InquiryContext.Provider value={value}>
            {children}
        </InquiryContext.Provider>
    );
};
